// Highlights on a record's text — the person's own marks. Anchored by the
// exact words plus where they were and a little context either side, so a
// re-analysis that changes the record's text can find them again
// (`locate`). Mirrors services/notes.py (COLOURS, MAX_CONTEXT).

export const HIGHLIGHT_COLOURS = [
  { id: 'yellow', label: 'Yellow', className: 'bg-mark-yellow' },
  { id: 'green', label: 'Green', className: 'bg-mark-green' },
  { id: 'blue', label: 'Blue', className: 'bg-mark-blue' },
  { id: 'pink', label: 'Pink', className: 'bg-mark-pink' },
  { id: 'lilac', label: 'Lilac', className: 'bg-mark-lilac' },
  { id: 'aqua', label: 'Aqua', className: 'bg-mark-aqua' },
];
export const colourClass = (id) => (HIGHLIGHT_COLOURS.find((c) => c.id === id) || HIGHLIGHT_COLOURS[0]).className;

const CONTEXT = 40;

export const contextOf = (text, start, end) => ({
  prefix: text.slice(Math.max(0, start - CONTEXT), start),
  suffix: text.slice(end, end + CONTEXT),
});

const commonSuffix = (a = '', b = '') => {
  let n = 0;
  while (n < a.length && n < b.length && a[a.length - 1 - n] === b[b.length - 1 - n]) n += 1;
  return n;
};
const commonPrefix = (a = '', b = '') => {
  let n = 0;
  while (n < a.length && n < b.length && a[n] === b[n]) n += 1;
  return n;
};

/**
 * Where a highlight is in `text` now: {start, end, moved} or null.
 * Exact position first; else every place the quote appears, scored by how
 * much of the saved context still surrounds it, nearest the old position on
 * a tie. Never guesses a passage that isn't there word for word.
 */
export function locate(text, h) {
  if (!text || !h?.quote) return null;
  const { quote } = h;
  if (text.slice(h.start, h.end) === quote) return { start: h.start, end: h.end, moved: false };
  let best = null;
  for (let i = text.indexOf(quote); i !== -1; i = text.indexOf(quote, i + 1)) {
    const score = commonSuffix(text.slice(Math.max(0, i - CONTEXT), i), h.prefix)
      + commonPrefix(text.slice(i + quote.length, i + quote.length + CONTEXT), h.suffix);
    const distance = Math.abs(i - (h.start || 0));
    if (!best || score > best.score || (score === best.score && distance < best.distance)) {
      best = { start: i, end: i + quote.length, moved: true, score, distance };
    }
  }
  return best ? { start: best.start, end: best.end, moved: true } : null;
}

/**
 * Split `text` into pieces at every highlight and redaction boundary.
 * `ranges`: [{start, end, id, colour}] (may overlap — the later one shows
 * on top). `redactions`: [{start, end}]. Returns
 * [{start, end, text, ids, colour, redaction}].
 */
export function pieces(text, ranges = [], redactions = []) {
  const cuts = new Set([0, text.length]);
  [...ranges, ...redactions].forEach((r) => {
    cuts.add(Math.max(0, Math.min(text.length, r.start)));
    cuts.add(Math.max(0, Math.min(text.length, r.end)));
  });
  const points = [...cuts].sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < points.length - 1; i += 1) {
    const s = points[i];
    const e = points[i + 1];
    if (e <= s) continue;
    const covering = ranges.filter((r) => r.start <= s && r.end >= e);
    out.push({
      start: s, end: e, text: text.slice(s, e),
      ids: covering.map((r) => r.id),
      colour: covering.length ? covering[covering.length - 1].colour : null,
      redaction: redactions.some((r) => r.start <= s && r.end >= e),
    });
  }
  return out;
}

const REDACTION_RE = /\[REDACTED(?::[^\]]+)?\]/g;
export function redactionRanges(text) {
  const out = [];
  if (!text) return out;
  REDACTION_RE.lastIndex = 0;
  let m;
  while ((m = REDACTION_RE.exec(text)) !== null) out.push({ start: m.index, end: m.index + m[0].length });
  return out;
}

/** Character offset of (node, offset) within `root`'s text. */
function offsetWithin(root, node, offset) {
  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(root, 4 /* NodeFilter.SHOW_TEXT */);
  let total = 0;
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (n === node) return total + offset;
    total += n.textContent.length;
  }
  // Selection ended on an element boundary: count the text before that child.
  if (node && node.nodeType === 1 && root.contains(node)) {
    let t = 0;
    const w2 = doc.createTreeWalker(root, 4);
    for (let n = w2.nextNode(); n; n = w2.nextNode()) {
      if (node.childNodes[offset] && (node.childNodes[offset] === n || node.childNodes[offset].contains(n))) return t;
      t += n.textContent.length;
    }
    return t;
  }
  return null;
}

/**
 * The current selection inside `root` as {start, end, quote} (trimmed of
 * surrounding spaces), or null if there isn't one there.
 */
export function selectionIn(root, selection = window.getSelection()) {
  if (!root || !selection || selection.rangeCount === 0 || selection.isCollapsed) return null;
  const range = selection.getRangeAt(0);
  if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return null;
  let start = offsetWithin(root, range.startContainer, range.startOffset);
  let end = offsetWithin(root, range.endContainer, range.endOffset);
  if (start == null || end == null) return null;
  if (end < start) [start, end] = [end, start];
  const text = root.textContent;
  while (start < end && /\s/.test(text[start])) start += 1;
  while (end > start && /\s/.test(text[end - 1])) end -= 1;
  if (end - start < 2) return null;
  return { start, end, quote: text.slice(start, end) };
}
